import {
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { backendErrorsHandler } from '../../utils/Notification/BackendErrorsHandler';
import { bulkCreatePaymentLines, bulkSetOrderCancelled, bulkSetOrderClean, bulkSetOrderCollect, createOperationalReturnOrder, createOrderUsersApp, createPaymentLineByOrderIdV2, deletePaymentLineByIdV2, getOrdersForCFDIV2, payAndCollect, previewOrderStoreTransfer, sendEmailReceipt, setOrdeDeliveredBySequenceV2, setOrdeDeliveringBySequenceV2, setOrdePickingBySequenceV2, setOrderAcceptedBySequenceV2, setOrderCancelledBySequenceV2, setOrderCleanedBySequenceV2, setOrderCleaningBySequenceV2, setOrderCollectedBySequenceV2, transferOrderStore, updateOrderByIdV2, updatePaymentLineByIdV2, createOrderEvidence, getRedeemPointsPreview, setOrderUncollected, sendOrderUncollectedCustomerNotification, updateLineStatuses, deleteOrderEvidence, updateOrderLineAssignments } from '../../services/order.service';
import { PaymentMethodsEnum } from '../../enum/PaymentMethodsEnum';
import { trackEvent } from '../../utils/monitoring/posthog';
import { MonitoringEventTypes } from '../../utils/monitoring/Monitoring_Events_Enum';
import { usePostHog } from 'posthog-js/react';
import type {
  CreateReturnOrderDto,
  IUpdateOrderLineAssignmentsDto,
  IOrderStoreTransferPreview,
  IOrderStoreTransferSuccess
} from 'washday-sdk';
import { returnOrdersQueryKey } from './queries';
const listQueryKey = 'orders';
const routesQueryKey = 'routes';

export const useCreateOperationalReturnOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceOrderId,
      data
    }: {
      sourceOrderId: string;
      data: CreateReturnOrderDto;
    }) => createOperationalReturnOrder(sourceOrderId, data),
    onSuccess: async (_response, variables) => Promise.all([
      queryClient.invalidateQueries({ queryKey: [listQueryKey] }),
      queryClient.invalidateQueries({
        queryKey: [returnOrdersQueryKey, variables.sourceOrderId]
      })
    ])
  });
};

export const usePreviewOrderStoreTransfer = () => useMutation({
  mutationFn: async ({
    orderId,
    targetStoreId
  }: {
    orderId: string;
    targetStoreId: string;
  }): Promise<IOrderStoreTransferPreview> => {
    const response = await previewOrderStoreTransfer(orderId, { targetStoreId });
    if (!response?.data?.data) {
      throw new Error('Order store transfer preview returned no data.');
    }
    return response.data.data;
  }
});

export const useTransferOrderStore = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      targetStoreId,
      targetCashierBoxId,
      acknowledgedWarnings
    }: {
      orderId: string;
      targetStoreId: string;
      targetCashierBoxId?: string;
      acknowledgedWarnings: boolean;
    }): Promise<IOrderStoreTransferSuccess> => {
      const response = await transferOrderStore(orderId, {
        targetStoreId,
        ...(targetCashierBoxId ? { targetCashierBoxId } : {}),
        acknowledgedWarnings
      });
      if (!response?.data?.data) {
        throw new Error('Order store transfer returned no data.');
      }
      return response.data.data as IOrderStoreTransferSuccess;
    },
    onSuccess: async () => Promise.all([
      queryClient.invalidateQueries({ queryKey: [listQueryKey] }),
      queryClient.invalidateQueries({ queryKey: ['outsourcingOrders'] }),
      queryClient.invalidateQueries({ queryKey: ['requestedOrdersSummary'] })
    ])
  });
};

const invalidateOrderAndRouteQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: [listQueryKey],
    }),
    queryClient.invalidateQueries({
      queryKey: [routesQueryKey],
    })
  ]);
};

export const useDeletePaymentLineById = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: { orderId: string, id: string }) => {
      try {
        const res = await deletePaymentLineByIdV2(data.orderId, data.id);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return invalidateOrderAndRouteQueries(queryClient);
    },
  });
};

export const useCreatePaymentLine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      orderId: string
      amountPaid: number
      cashierBox: string
      paymentMethod: string
      paymentDate: string
      pinUserId?: string
    }) => {
      try {
        const res = await createPaymentLineByOrderIdV2(data.orderId, {
          amountPaid: data.amountPaid,
          cashierBox: data.cashierBox,
          paymentMethod: data.paymentMethod,
          paymentDate: data.paymentDate,
          pinUserId: data.pinUserId
        });
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      })]);
    }
  });
};

export const useUpdatePayementLineById = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      orderId: string
      id: string,
      amountPaid: number
      paymentMethod?: PaymentMethodsEnum.Cash | PaymentMethodsEnum.Card | PaymentMethodsEnum.Transfer
      updatedDate: Date
      pinUserId?: string
    }) => {
      try {
        const res = await updatePaymentLineByIdV2(data.orderId, data.id, {
          amountPaid: data.amountPaid,
          paymentMethod: data.paymentMethod,
          updatedDate: data.updatedDate,
          pinUserId: data.pinUserId
        });
        if (!res?.data) {
          throw new Error('Oh no! Something went wrong while updating.');
        }
        return res.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      //INVALIDATE CACHED  WITH STORE ID AND ID
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      })]);
    }
  });
};

export const useSendEmailReceipt = () => {
  return useMutation({
    mutationFn: async (orderId: string) => {
      try {
        const res = await sendEmailReceipt(orderId);
        if (!res?.data) {
          throw new Error('Oh no! Something went wrong while updating.');
        }
        return res.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    }
  });
};

export const useGetCFDIOrders = () => {
  return useMutation({
    mutationFn: async (params: {
      fromDate: string
      toDate: string
      storeID?: string
      storeIds?: string[]
      includeAllStores?: boolean
      customerID?: string
    }) => {
      try {
        const res = await getOrdersForCFDIV2(params);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    }
  });
};

export const useSetOrderCancelled = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      sequence: string, storeId: string,
      data: {
        orderId?: string
        cancelledDateTime: string
      }
    }) => {
      try {
        const res = await setOrderCancelledBySequenceV2(data.sequence, data.storeId, data.data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return invalidateOrderAndRouteQueries(queryClient);
    },
  });
};

export const useSetOrderCleaning = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      sequence: string, storeId: string,
      params?: {
        markOutsourcedAsReturned?: boolean
      }
    }) => {
      try {
        const res = await setOrderCleaningBySequenceV2(data.sequence, data.storeId);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return invalidateOrderAndRouteQueries(queryClient);
    },
  });
};

export const useSetOrderCleaned = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      sequence: string, storeId: string,
      data: {
        cleanedDateTime: string,
        readyDateTime: string
      },
      params?: {
        markOutsourcedAsReturned?: boolean
      }
    }) => {
      try {
        const res = await setOrderCleanedBySequenceV2(data.sequence, data.storeId, data.data, data.params);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return invalidateOrderAndRouteQueries(queryClient);
    },
  });
};

export const useSetOrderCollected = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      sequence: string,
      storeId: string,
      data: {
        collectedDateTime: string
        collectWithAmountDue?: boolean
        orderId?: string
        expectedStatus?: 'ready'
        pinValidationToken?: string
        pinUserId?: string
      }
    }) => {
      try {
        const res = await setOrderCollectedBySequenceV2(data.sequence, data.storeId, data.data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return invalidateOrderAndRouteQueries(queryClient);
    },
  });
};

export const useSetOrderPicking = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      sequence: string,
      storeId: string,
      data: {
        pickingUpDateTime: string
      }
    }) => {
      try {
        const res = await setOrdePickingBySequenceV2(data.sequence, data.storeId, data.data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return invalidateOrderAndRouteQueries(queryClient);
    },
  });
};

export const useSetOrderDelivering = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      sequence: string,
      storeId: string,
      data: {
        deliveringDateTime: string
      }
    }) => {
      try {
        const res = await setOrdeDeliveringBySequenceV2(data.sequence, data.storeId, data.data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      })]);
    },
  });
};

export const useSetOrderDelivered = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      sequence: string,
      storeId: string,
      data: {
        collectedDateTime: string
        collectWithAmountDue?: boolean
        pinUserId?: string
        pinValidationToken?: string
      }
    }) => {
      try {
        const res = await setOrdeDeliveredBySequenceV2(data.sequence, data.storeId, data.data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      })]);
    },
  });
};

export const usePayAndCollect = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      orderId?: string;
      sequence: string;
      storeId: string;
      paymentMethod: string;
      cashierBoxId?: string;
      amount?: number;
      paymentDate?: string | Date;
      collectedDateTime?: string | Date;
      collectWithAmountDue?: boolean;
      pinValidationToken?: string;
      pinUserId?: string;
      attemptId?: string;
      expectedStatus?: 'ready';
    }) => {
      try {
        const res = await payAndCollect(data);
        if (!res?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data;
      } catch (e: any) {
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return invalidateOrderAndRouteQueries(queryClient);
    },
  });
};

export const useSetOrderAccepted = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  return useMutation({
    mutationFn: async (data: {
      sequence: string, storeId: string
    }) => {
      try {
        const res = await setOrderAcceptedBySequenceV2(data.sequence, data.storeId);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        trackEvent(posthog, MonitoringEventTypes.ORDER_ACCEPTED, {});
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      })]);
    },
  });
};

export const useBulkSetOrderCancelled = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      storeId: string,
      body: {
        ordersIds: string[];
        cancelledDateTime: string;
      }
    }) => {
      try {
        const res = await bulkSetOrderCancelled(data.storeId, data.body);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      })]);
    },
  });
};

export const useBulkSetOrderCleaned = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      storeId: string,
      body: {
        ordersIds: string[];
        cleanedDateTime: string;
        readyDateTime: string;
      },
      params?: {
        markOutsourcedAsReturned?: boolean
      }
    }) => {
      try {
        const res = await bulkSetOrderClean(data.storeId, data.body, data.params);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      })]);
    },
  });
};

export const useBulkSetOrderCollected = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      storeId: string,
      body: {
        pinUserId?: string;
        pinValidationToken?: string;
        collectWithAmountDue?: boolean;
        ordersIds: string[];
        collectedDateTime: string;
      }
    }) => {
      try {
        const res = await bulkSetOrderCollect(data.storeId, data.body);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      })]);
    },
  });
};

export const useBulkCreatePaymentLines = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      storeId: string,
      body: {
        paymentLines: {
          sequence: string;
          orderId: string
          paymentMethod: PaymentMethodsEnum
          amount: number;
          cashierBoxId: string;
          paymentDate: Date;
          pinUserId?: string
        }[],
      }
    }) => {
      try {
        const res = await bulkCreatePaymentLines(data.storeId, data.body);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      })]);
    },
  });
};

export const useCreateOrder = () => {
  return useMutation({
    mutationFn: async (data: any) => {
      try {
        const res = await createOrderUsersApp(data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    }
  });
};

export const useUpdateOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: {
      id: string; data: {
        orderType?: 'standard' | 'return',
        returnSourceOrderId?: string,
        returnItemsChargeMode?: 'no_charge' | 'charge',
        status?: string,
        pinUserId?: string,
        pinValidationToken?: string,
        products?: any[],
        buyAndGetProducts?: any[],
        customer?: string,
        discountCode?: string | null,
        notes?: string,
        privateNotes?: string,
        delivery?: boolean,
        pickup?: boolean,
        express?: boolean,
        deliveryInfo?: {
          date: Date,
          fromTime: string,
          toTime?: string
        },
        pickupInfo?: {
          date: Date,
          fromTime: string,
          toTime?: string
        },
        notifyBy?: string,
        updateMode?: 'store_reception_validation',
      }
    }) => {
      try {
        const res = await updateOrderByIdV2(id, data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        throw backendErrorsHandler(e);
      }
    },
    onSuccess: async (_data, variables) => {
      const { id } = variables;
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey, { _id: id }],
      })]);
    }
  });
};

export const useUpdateOrderLineAssignments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orderId,
      updates
    }: IUpdateOrderLineAssignmentsDto & { orderId: string }) => {
      try {
        const res = await updateOrderLineAssignments(orderId, { updates });
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while updating.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        throw backendErrorsHandler(e);
      }
    },
    onSuccess: async (_data, variables) => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [listQueryKey, { _id: variables.orderId }] }),
        queryClient.invalidateQueries({ queryKey: [listQueryKey] })
      ]);
    }
  });
};


export const useCreateOrderEvidence = () => {
  return useMutation({
    mutationFn: async (payload: {
      orderId: string,
      data: FormData
    }) => {
      try {
        const { orderId, data } = payload;
        const res = await createOrderEvidence(orderId, data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    }
  });
};

export const useDeleteOrderEvidence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      orderId: string,
      evidenceId: string
    }) => {
      try {
        const { orderId, evidenceId } = payload;
        const res = await deleteOrderEvidence(orderId, evidenceId);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while deleting.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return invalidateOrderAndRouteQueries(queryClient);
    },
  });
};

export const useGetRedeemPointsPreview = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      customerId,
      storeId,
      orderTotal,
    }: {
      customerId: string,
      storeId: string,
      orderTotal: number
    }) => {
      try {
        const res = await getRedeemPointsPreview({ customerId, storeId, orderTotal });
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      })]);
    },
  });
};

export const useSetOrderUncollected = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      orderId?: string;
      orderSequence?: string;
      orderStore?: string;
      reason?: 'abandoned' | 'sent_to_warehouse' | 'donated' | 'discarded' | 'other';
    }) => {
      try {
        const res = await setOrderUncollected(data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      })]);
    },
  });
};

export const useSendOrderUncollectedCustomerNotification = () => {
  return useMutation({
    mutationFn: async (orderId: string) => {
      try {
        const res = await sendOrderUncollectedCustomerNotification(orderId);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
  });
};

export const useUpdateLineStatuses = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      orderId: string;
      updates: Array<{ lineId: string; status: string }>;
    }) => {
      try {
        const res = await updateLineStatuses(data.orderId, data.updates);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async (_, variables) => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [listQueryKey, { _id: variables.orderId }] }),
        queryClient.invalidateQueries({ queryKey: [listQueryKey] }),
      ]);
    },
  });
};
